/**
 * 店舗サービス管理: 店舗関連のQuery Hooks
 * TanStack Queryを使用したデータフェッチングとキャッシュ管理
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthHeader } from '@/hooks/useAuth';
import {
  getSellersCurrentAuthenticated,
  getSellersNextAuthenticated,
  updateSellersNextAuthenticated,
  getSellerGroupsCurrentAuthenticated,
  getSellerGroupsNextAuthenticated,
  updateSellerGroupsNextAuthenticated,
  getSellerSellerGroupMapCurrentAuthenticated,
  getSellerSellerGroupMapNextAuthenticated,
  updateSellerSellerGroupMapNextAuthenticated,
} from '@/lib/api/mise/sellers';
import {
  miseSellerKeys,
  miseSellerGroupCurrentNextKeys,
  miseSellerSellerGroupMapKeys,
} from '../keys';
import { CACHE_TIME } from '../client';
import type {
  UpdateSellersNextRequest,
  UpdateSellerGroupsNextRequest,
  UpdateSellerSellerGroupMapNextRequest,
} from '@/lib/types/mise/seller';

/**
 * 集計後の店舗一覧を取得するQuery Hook
 * @param accountId アカウントID
 */
export function useMiseSellersCurrent(accountId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseSellerKeys.current(accountId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getSellersCurrentAuthenticated(accountId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!accountId, // 認証状態が準備完了し、IDが存在する場合のみ実行
    staleTime: CACHE_TIME.ACCOUNT_DETAIL, // アカウント詳細と同じキャッシュ時間を使用
  });
}

/**
 * 集計前の店舗一覧を取得するQuery Hook
 * @param accountId アカウントID
 */
export function useMiseSellersNext(accountId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseSellerKeys.next(accountId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getSellersNextAuthenticated(accountId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!accountId, // 認証状態が準備完了し、IDが存在する場合のみ実行
    staleTime: CACHE_TIME.ACCOUNT_DETAIL, // アカウント詳細と同じキャッシュ時間を使用
  });
}

/**
 * 集計前の店舗一覧を更新するMutation Hook
 * @param accountId アカウントID
 */
export function useUpdateSellersNext(accountId: string) {
  const { getAuthHeader } = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateSellersNextRequest) => {
      const authHeaders = getAuthHeader();
      return updateSellersNextAuthenticated(accountId, request, authHeaders);
    },
    onSuccess: () => {
      // 更新成功時に集計前の店舗一覧のクエリを無効化して再取得
      queryClient.invalidateQueries({ queryKey: miseSellerKeys.next(accountId) });
    },
  });
}

/**
 * 集計後の店舗グループ一覧を取得するQuery Hook
 * @param accountId アカウントID
 */
export function useMiseSellerGroupsCurrent(accountId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseSellerGroupCurrentNextKeys.current(accountId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getSellerGroupsCurrentAuthenticated(accountId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!accountId,
    staleTime: CACHE_TIME.ACCOUNT_DETAIL,
  });
}

/**
 * 集計前の店舗グループ一覧を取得するQuery Hook
 * @param accountId アカウントID
 */
export function useMiseSellerGroupsNext(accountId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseSellerGroupCurrentNextKeys.next(accountId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getSellerGroupsNextAuthenticated(accountId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!accountId,
    staleTime: CACHE_TIME.ACCOUNT_DETAIL,
  });
}

/**
 * 集計前の店舗グループ一覧を更新するMutation Hook
 * @param accountId アカウントID
 */
export function useUpdateSellerGroupsNext(accountId: string) {
  const { getAuthHeader } = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateSellerGroupsNextRequest) => {
      const authHeaders = getAuthHeader();
      return updateSellerGroupsNextAuthenticated(accountId, request, authHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: miseSellerGroupCurrentNextKeys.next(accountId),
      });
      // 店舗グループが変わるとマッピングにも影響する可能性がある
      queryClient.invalidateQueries({
        queryKey: miseSellerSellerGroupMapKeys.next(accountId),
      });
    },
  });
}

/**
 * 集計後の店舗-店舗グループマッピングを取得するQuery Hook
 * @param accountId アカウントID
 */
export function useMiseSellerSellerGroupMapCurrent(accountId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseSellerSellerGroupMapKeys.current(accountId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getSellerSellerGroupMapCurrentAuthenticated(accountId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!accountId,
    staleTime: CACHE_TIME.ACCOUNT_DETAIL,
  });
}

/**
 * 集計前の店舗-店舗グループマッピングを取得するQuery Hook
 * @param accountId アカウントID
 */
export function useMiseSellerSellerGroupMapNext(accountId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseSellerSellerGroupMapKeys.next(accountId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getSellerSellerGroupMapNextAuthenticated(accountId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!accountId,
    staleTime: CACHE_TIME.ACCOUNT_DETAIL,
  });
}

/**
 * 集計前の店舗-店舗グループマッピングを更新するMutation Hook
 * @param accountId アカウントID
 */
export function useUpdateSellerSellerGroupMapNext(accountId: string) {
  const { getAuthHeader } = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateSellerSellerGroupMapNextRequest) => {
      const authHeaders = getAuthHeader();
      return updateSellerSellerGroupMapNextAuthenticated(accountId, request, authHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: miseSellerSellerGroupMapKeys.next(accountId),
      });
    },
  });
}
